/**
 * Admin screen: CRUD for Firestore collection `eventTypes`.
 * Table + expandable form panels (same pattern as FAQ / articles).
 */

import { useState } from "react";
import { useMediaQuery } from "react-responsive";
import {
  BackScreenDiv,
  Button,
  ContentDiv,
  Div,
  H1,
  HeaderDiv,
  ScreenDiv,
  P,
  TableColumnDiv,
  TableRowDivEventTypes,
  UserTableBodyDiv,
  UserTableDiv,
  UserTableRowDiv,
  AdminEventTypesCard,
  AdminEventTypeFieldLabel,
  AdminEventTypeTextInput,
  AdminEventTypeSelect,
  AdminEventTypeLangBlock,
  AdminEventTypeIdKeyRow,
  AdminEventTypeTripleGrid,
  AdminEventTypeLangGrid,
  AdminEventTypeCheckboxRow,
  AdminEventTypeCheckboxLabel,
} from "../../../styles/styles";

const LANGUAGES = ["en", "ru", "sr"];

const CATEGORY_OPTIONS = [
  { value: "prevention", label: "prevention" },
  { value: "veterinary", label: "veterinary" },
  { value: "care", label: "care" },
  { value: "leisure", label: "leisure" },
  { value: "training", label: "training" },
  { value: "custom", label: "custom" },
  { value: "other", label: "other" },
];

const EVENT_TYPE_CREATE_FORM_ID = "event-type-create-form";
const eventTypeEditFormId = (id) => `event-type-edit-form-${id}`;

const emptyFormFields = () => ({
  newDocId: "",
  key: "",
  category: "prevention",
  order: 0,
  enabledByDefault: true,
  iconUrl: "",
  labels: { en: "", ru: "", sr: "" },
  supportsMedication: false,
});

function rowToForm(row) {
  return {
    newDocId: "",
    key: row.key || "",
    category: row.category || "other",
    order: row.order ?? 0,
    enabledByDefault: row.enabledByDefault !== false,
    iconUrl: row.iconUrl || "",
    labels: {
      en: row.labels?.en || "",
      ru: row.labels?.ru || "",
      sr: row.labels?.sr || "",
    },
    supportsMedication: !!row.supportsMedication,
  };
}

function labelPreview(row) {
  return (
    row.labels?.en ||
    row.labels?.ru ||
    row.labels?.sr ||
    row.key ||
    row.id ||
    "—"
  );
}

function EventTypeFormBody({
  form,
  setForm,
  setLabel,
  isDesktop,
  isNewDoc,
  docIdReadonly,
  formError,
}) {
  const keyLabel = isDesktop
    ? "key (поле eventType в событиях)"
    : "key → в событиях как eventType (обязательно)";

  return (
    <Div gap="8px" width="100%" justifyStart alignStart>
      {isNewDoc ? (
        <AdminEventTypeIdKeyRow>
          <Div width="100%" alignStart justifyStart>
            <AdminEventTypeFieldLabel>
              ID документа · опционально
            </AdminEventTypeFieldLabel>
            <AdminEventTypeTextInput
              value={form.newDocId}
              onChange={(e) =>
                setForm((p) => ({ ...p, newDocId: e.target.value }))
              }
              placeholder="tick-flea"
              autoComplete="off"
            />
          </Div>
          <Div width="100%" alignStart justifyStart>
            <AdminEventTypeFieldLabel>{keyLabel}</AdminEventTypeFieldLabel>
            <AdminEventTypeTextInput
              value={form.key}
              onChange={(e) => setForm((p) => ({ ...p, key: e.target.value }))}
              placeholder="Tick and Flea"
              required
            />
          </Div>
        </AdminEventTypeIdKeyRow>
      ) : (
        <>
          <P
            fontSize="11px"
            margin="0"
            textAlign="left"
            style={{ opacity: 0.8, lineHeight: 1.3 }}
          >
            Doc ID: <strong>{docIdReadonly}</strong>
          </P>
          <Div width="100%" alignStart justifyStart>
            <AdminEventTypeFieldLabel>{keyLabel}</AdminEventTypeFieldLabel>
            <AdminEventTypeTextInput
              value={form.key}
              onChange={(e) => setForm((p) => ({ ...p, key: e.target.value }))}
              placeholder="Tick and Flea"
              required
            />
          </Div>
        </>
      )}

      <AdminEventTypeTripleGrid>
        <Div width="100%" alignStart justifyStart>
          <AdminEventTypeFieldLabel>Категория</AdminEventTypeFieldLabel>
          <AdminEventTypeSelect
            value={form.category}
            onChange={(e) =>
              setForm((p) => ({ ...p, category: e.target.value }))
            }
          >
            {CATEGORY_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </AdminEventTypeSelect>
        </Div>
        <Div width="100%" alignStart justifyStart>
          <AdminEventTypeFieldLabel>Порядок</AdminEventTypeFieldLabel>
          <AdminEventTypeTextInput
            type="number"
            value={form.order}
            onChange={(e) =>
              setForm((p) => ({
                ...p,
                order: Number(e.target.value) || 0,
              }))
            }
          />
        </Div>
        <Div width="100%" alignStart justifyStart>
          <AdminEventTypeFieldLabel>URL иконки</AdminEventTypeFieldLabel>
          <AdminEventTypeTextInput
            value={form.iconUrl}
            onChange={(e) => setForm((p) => ({ ...p, iconUrl: e.target.value }))}
            placeholder="https://…"
          />
        </Div>
      </AdminEventTypeTripleGrid>

      <AdminEventTypeCheckboxRow>
        <AdminEventTypeCheckboxLabel>
          <input
            type="checkbox"
            checked={form.enabledByDefault}
            onChange={(e) =>
              setForm((p) => ({
                ...p,
                enabledByDefault: e.target.checked,
              }))
            }
          />
          По умолчанию вкл.
        </AdminEventTypeCheckboxLabel>
        <AdminEventTypeCheckboxLabel>
          <input
            type="checkbox"
            checked={form.supportsMedication}
            onChange={(e) =>
              setForm((p) => ({
                ...p,
                supportsMedication: e.target.checked,
              }))
            }
          />
          Препараты
        </AdminEventTypeCheckboxLabel>
      </AdminEventTypeCheckboxRow>

      <AdminEventTypeLangGrid>
        {LANGUAGES.map((lang) => (
          <AdminEventTypeLangBlock key={lang} width="100%" alignStart>
            <AdminEventTypeFieldLabel>{lang.toUpperCase()}</AdminEventTypeFieldLabel>
            <AdminEventTypeTextInput
              value={form.labels[lang] || ""}
              onChange={(e) => setLabel(lang, e.target.value)}
              placeholder={`Подпись (${lang})`}
            />
          </AdminEventTypeLangBlock>
        ))}
      </AdminEventTypeLangGrid>

      {formError && (
        <div role="alert" style={{ color: "#c62828", fontSize: 12 }}>
          {formError}
        </div>
      )}
    </Div>
  );
}

function EventTypeFormPanel({
  title,
  formId,
  form,
  setForm,
  setLabel,
  isDesktop,
  isNewDoc,
  docIdReadonly,
  formError,
  onSubmit,
  onCancel,
  onDelete,
  saving,
}) {
  return (
    <AdminEventTypesCard
      padding="14px 16px"
      justifyStart
      alignStart
      style={{ margin: "0 0 8px 0" }}
    >
      <H1 fontSize="15px" align="left" style={{ margin: "0 0 10px 0" }}>
        {title}
      </H1>
      <form id={formId} onSubmit={onSubmit} style={{ width: "100%", textAlign: "left" }}>
        <EventTypeFormBody
          form={form}
          setForm={setForm}
          setLabel={setLabel}
          isDesktop={isDesktop}
          isNewDoc={isNewDoc}
          docIdReadonly={docIdReadonly}
          formError={formError}
        />
        <Div row gap="8px" margin="12px 0 0 0" justifyStart alignStart wrap="wrap">
          <Button
            type="submit"
            greenButton
            width="auto"
            disabled={saving}
            fontSize="13px"
            padding="7px 14px"
            height="auto"
          >
            {saving ? "Saving…" : isNewDoc ? "Create" : "Update"}
          </Button>
          <Button
            type="button"
            outlineButton
            width="auto"
            onClick={onCancel}
            fontSize="13px"
            padding="7px 14px"
            height="auto"
          >
            Close
          </Button>
          {!isNewDoc && onDelete ? (
            <Button
              type="button"
              deleteButton
              width="auto"
              disabled={saving}
              onClick={onDelete}
              fontSize="13px"
              padding="7px 14px"
              height="auto"
            >
              Delete
            </Button>
          ) : null}
        </Div>
      </form>
    </AdminEventTypesCard>
  );
}

export default function AdminEventTypes({
  items,
  loading,
  saving,
  error,
  onRefresh,
  onSave,
  onDelete,
}) {
  const isDesktop = useMediaQuery({ minWidth: "800px" });
  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState(emptyFormFields);
  const [createError, setCreateError] = useState(null);

  const [editingDocId, setEditingDocId] = useState(null);
  const [editForm, setEditForm] = useState(emptyFormFields);
  const [editError, setEditError] = useState(null);

  const setCreateLabel = (lang, value) => {
    setCreateForm((prev) => ({
      ...prev,
      labels: { ...prev.labels, [lang]: value },
    }));
  };

  const setEditLabel = (lang, value) => {
    setEditForm((prev) => ({
      ...prev,
      labels: { ...prev.labels, [lang]: value },
    }));
  };

  const openCreate = () => {
    setEditingDocId(null);
    setCreateOpen(true);
    setCreateForm(emptyFormFields());
    setCreateError(null);
  };

  const closeCreate = () => {
    setCreateOpen(false);
    setCreateForm(emptyFormFields());
    setCreateError(null);
  };

  const handleEditToggle = (row) => {
    if (editingDocId === row.id) {
      setEditingDocId(null);
      setEditForm(emptyFormFields());
      setEditError(null);
      return;
    }
    setCreateOpen(false);
    setEditingDocId(row.id);
    setEditForm(rowToForm(row));
    setEditError(null);
  };

  const submitCreate = async (e) => {
    e.preventDefault();
    setCreateError(null);
    const result = await onSave({
      editingId: null,
      newDocId: createForm.newDocId,
      key: createForm.key,
      category: createForm.category,
      order: createForm.order,
      enabledByDefault: createForm.enabledByDefault,
      iconUrl: createForm.iconUrl,
      labels: createForm.labels,
      supportsMedication: createForm.supportsMedication,
    });
    if (result?.ok) {
      closeCreate();
    } else if (result?.error) {
      setCreateError(result.error);
    }
  };

  const submitEdit = async (e) => {
    e.preventDefault();
    if (!editingDocId) return;
    setEditError(null);
    const result = await onSave({
      editingId: editingDocId,
      newDocId: "",
      key: editForm.key,
      category: editForm.category,
      order: editForm.order,
      enabledByDefault: editForm.enabledByDefault,
      iconUrl: editForm.iconUrl,
      labels: editForm.labels,
      supportsMedication: editForm.supportsMedication,
    });
    if (result?.ok) {
      setEditingDocId(null);
      setEditForm(emptyFormFields());
      setEditError(null);
    } else if (result?.error) {
      setEditError(result.error);
    }
  };

  const handleDelete = async () => {
    if (!editingDocId) return;
    const result = await onDelete(editingDocId);
    if (result?.ok) {
      setEditingDocId(null);
      setEditForm(emptyFormFields());
      setEditError(null);
    }
  };

  return (
    <>
      <BackScreenDiv grayImage />

      <ScreenDiv column mobileJustifySpace>
        <HeaderDiv />

        <ContentDiv
          gap={!isDesktop && "5px"}
          paddingBack={isDesktop}
          borderRadius="30px"
          alignStart
          justifyStart
          height="auto"
          heightDesktop="auto"
        >
          <Div row width="100%" justify="space-between" align="center">
            {isDesktop && <H1>Event Types</H1>}
            <Button
              type="button"
              onClick={onRefresh}
              disabled={loading}
              width="auto"
              greenButton
            >
              {loading ? "Loading…" : "Refresh"}
            </Button>
          </Div>

          {error && (
            <div role="alert" style={{ color: "#c62828", marginBottom: 8 }}>
              {error}
            </div>
          )}

          <Div row gap="10px" justifyStart width="100%" height="auto">
            <Button
              type="button"
              greenButton
              width="auto"
              disabled={createOpen}
              onClick={openCreate}
            >
              Create
            </Button>
            {createOpen ? (
              <>
                <Button
                  type="submit"
                  form={EVENT_TYPE_CREATE_FORM_ID}
                  greenButton
                  width="auto"
                  disabled={saving}
                >
                  {saving ? "Saving…" : "Create"}
                </Button>
                <Button
                  type="button"
                  outlineButton
                  width="auto"
                  onClick={closeCreate}
                >
                  Close
                </Button>
              </>
            ) : null}
          </Div>

          {createOpen ? (
            <EventTypeFormPanel
              title="Create — Event Type"
              formId={EVENT_TYPE_CREATE_FORM_ID}
              form={createForm}
              setForm={setCreateForm}
              setLabel={setCreateLabel}
              isDesktop={isDesktop}
              isNewDoc
              docIdReadonly={null}
              formError={createError}
              onSubmit={submitCreate}
              onCancel={closeCreate}
              saving={saving}
            />
          ) : null}

          <Div width="100%">
            <UserTableDiv>
              <TableRowDivEventTypes>
                <TableColumnDiv>Label</TableColumnDiv>
                <TableColumnDiv>ID</TableColumnDiv>
                <TableColumnDiv>Category</TableColumnDiv>
                <TableColumnDiv>Order</TableColumnDiv>
                <TableColumnDiv>Edit</TableColumnDiv>
              </TableRowDivEventTypes>

              <UserTableBodyDiv>
                {items.map((row) => {
                  const preview = labelPreview(row);
                  const isEditing = editingDocId === row.id;

                  return (
                    <UserTableRowDiv key={row.id}>
                      <TableRowDivEventTypes>
                        <TableColumnDiv textAlign="left">{preview}</TableColumnDiv>
                        <TableColumnDiv textAlign="left">{row.id}</TableColumnDiv>
                        <TableColumnDiv textAlign="left">
                          {row.category || "—"}
                        </TableColumnDiv>
                        <TableColumnDiv textAlign="left">
                          {row.order ?? 0}
                        </TableColumnDiv>
                        <TableColumnDiv>
                          <Div row gap="6px" justifyStart wrap="wrap">
                            {isEditing ? (
                              <Button
                                type="submit"
                                form={eventTypeEditFormId(row.id)}
                                greenButton
                                width="auto"
                                fontSize="12px"
                                height="auto"
                                padding="5px 10px"
                                disabled={saving}
                              >
                                {saving ? "Saving…" : "Update"}
                              </Button>
                            ) : null}
                            <Button
                              type="button"
                              onClick={() => handleEditToggle(row)}
                              width="auto"
                              outlineButtonGreen
                              fontSize="12px"
                              height="auto"
                              padding="5px 10px"
                            >
                              {isEditing ? "Close" : "Edit"}
                            </Button>
                          </Div>
                        </TableColumnDiv>
                      </TableRowDivEventTypes>

                      {isEditing ? (
                        <EventTypeFormPanel
                          title={`Edit — ${preview}`}
                          formId={eventTypeEditFormId(row.id)}
                          form={editForm}
                          setForm={setEditForm}
                          setLabel={setEditLabel}
                          isDesktop={isDesktop}
                          isNewDoc={false}
                          docIdReadonly={row.id}
                          formError={editError}
                          onSubmit={submitEdit}
                          onCancel={() => handleEditToggle(row)}
                          onDelete={handleDelete}
                          saving={saving}
                        />
                      ) : null}
                    </UserTableRowDiv>
                  );
                })}
                {items.length === 0 && !loading ? (
                  <TableColumnDiv style={{ padding: 12 }} textAlign="left">
                    No event types yet — click Create.
                  </TableColumnDiv>
                ) : null}
              </UserTableBodyDiv>
            </UserTableDiv>
          </Div>
        </ContentDiv>
      </ScreenDiv>
    </>
  );
}
